import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import type { CombustivelTipo, CondicaoTipo, LeadStatus, MoedaTipo } from "./types"
import type { Translations } from "./i18n/locales"
import type { Locale } from "./i18n/locales"
import { resolveI18n, type I18nText } from "./i18n/resolve"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Classes aplicadas a barras/painéis de filtro enquanto uma navegação está pendente.
// Esmaece o controle, bloqueia novos cliques e mostra cursor-wait — feedback claro de
// que o filtro foi capturado e está sendo aplicado.
export const FILTER_PENDING_CLASSES =
  "pointer-events-none opacity-60 cursor-wait transition-opacity"

// --- Normalizacao de nomes (sempre uppercase) ---

export function uppercaseName(value: unknown): string | null {
  if (typeof value !== "string") return null
  const trimmed = value.trim()
  return trimmed ? trimmed.toUpperCase() : null
}

// --- Normalizacao / formatacao de prefixo (matricula) ---

/**
 * Forma canonica de armazenamento da matricula: so A-Z0-9 maiusculo, sem hifen
 * nem caracteres especiais. Retorna null quando nao sobra nada (campo vazio).
 * Aceita prefixos de qualquer pais e tamanho (ex: PTABC, N12345, GABCD).
 */
export function normalizePrefixo(raw: string | null | undefined): string | null {
  if (!raw) return null
  const clean = raw.replace(/[^A-Za-z0-9]/g, "").toUpperCase()
  return clean.length > 0 ? clean : null
}

// Padrao brasileiro: P + [PRSTU] + 3 alfanumericos (ex: PTABC, PRHUC, PSMLV).
const BR_PREFIXO_RE = /^P[PRSTU][A-Z0-9]{3}$/

/**
 * Exibicao da matricula. So o padrao brasileiro recebe hifen
 * (PTABC -> PT-ABC). Prefixos estrangeiros sao exibidos limpos, sem hifen
 * (N12345 -> N12345), porque o ponto do hifen varia por pais.
 */
export function formatPrefixo(raw: string | null): string {
  const clean = normalizePrefixo(raw)
  if (!clean) return "—"
  if (BR_PREFIXO_RE.test(clean)) return `${clean.slice(0, 2)}-${clean.slice(2)}`
  return clean
}

// --- Formatacao de moeda ---

export function getMoedaSymbol(moeda: MoedaTipo): string {
  const symbols: Record<MoedaTipo, string> = {
    BRL: 'R$',
    USD: 'US$',
    EUR: '€',
  }
  return symbols[moeda]
}

export function formatCurrency(valor: number | null, moeda: MoedaTipo = 'USD'): string {
  if (valor === null || valor === undefined) return 'Consulte-nos'

  if (moeda === 'USD') {
    const num = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(valor)
    return `USD ${num}`
  }

  if (moeda === 'EUR') {
    const num = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(valor)
    return `EUR ${num}`
  }

  // BRL — formato brasileiro
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(valor)
}

export function formatPriceInput(value: string, moeda: MoedaTipo): string {
  const digits = value.replace(/\D/g, '')
  if (!digits) return ''
  const separator = moeda === 'BRL' ? '.' : ','
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, separator)
}

export function parsePriceInput(value: string): string {
  return value.replace(/\D/g, '')
}

// --- Formatacao de horas ---

export function formatHoras(horas: number | null): string {
  if (horas === null || horas === undefined) return '—'
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(horas) + ' h'
}

// --- Slugify ---

export function slugify(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // remove acentos
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
}

// Remove `${prefix}-` do in\u00edcio de `target` se houver. Preserva a igualdade
// total (target === prefix) intacta \u2014 quem chama escolhe o que fazer.
function stripSlugPrefix(target: string, prefix: string): string {
  if (target === prefix) return target
  if (target.startsWith(`${prefix}-`)) return target.slice(prefix.length + 1)
  return target
}

// Slug determin\u00edstico de aeronave:
//   {fabricante}-{categoria}-{modelo}[-{ano}]-{referencia-sanitizada}
// Como `aeronaves.referencia` \u00e9 UNIQUE, o slug \u00e9 \u00fanico por constru\u00e7\u00e3o.
// Dedupe de prefixo: se `modeloSlug` j\u00e1 come\u00e7a com `fabricanteSlug`
// (ex: modelo "cirrus-aircraft-sr22" em fabricante "cirrus-aircraft"),
// remove o prefixo para n\u00e3o duplicar. Mesma l\u00f3gica para categoria.
export function buildAeronaveSlug(parts: {
  fabricanteSlug: string
  categoriaSlug: string
  modeloSlug: string
  anoFabricacao: number | null | undefined
  referencia: string
}): string {
  const modeloDedup = stripSlugPrefix(parts.modeloSlug, parts.fabricanteSlug)
  const modeloFinal = stripSlugPrefix(modeloDedup, parts.categoriaSlug)
  const ano = parts.anoFabricacao ? `-${parts.anoFabricacao}` : ''
  return `${parts.fabricanteSlug}-${parts.categoriaSlug}-${modeloFinal}${ano}-${slugify(parts.referencia)}`
}

// Título SEO determinístico (auto-fill na criação):
//   FABRICANTE MODELO ANO - Aviadores
// Nomes próprios não variam por idioma; devolve o mesmo texto nos 3 locales.
export function buildAeronaveTituloSeo(parts: {
  fabricanteNome: string
  modeloNome: I18nText
  anoFabricacao: number | null | undefined
}): Record<Locale, string> {
  const fabricante = parts.fabricanteNome.trim().toUpperCase()
  const modelo = resolveI18n(parts.modeloNome, 'pt-BR').trim().toUpperCase()
  const ano = parts.anoFabricacao ? String(parts.anoFabricacao) : ''
  const base = [fabricante, modelo, ano].filter(Boolean).join(' ')
  const text = base ? `${base} - Aviadores` : 'Aviadores'
  return {
    'pt-BR': text,
    'en-US': text,
    es: text,
  }
}

// --- Template de mensagem configuravel (aba "Mensagem" das aeronaves) ---

export const MESSAGE_TEMPLATE_VARIABLES = [
  'fabricante',
  'modelo',
  'preco',
  'ano',
  'prefixo',
  'prefixo_sem_hifen',
  'horas_celula',
  'referencia',
  'url',
] as const

export type MessageTemplateVariable = (typeof MESSAGE_TEMPLATE_VARIABLES)[number]

export type MessageTemplateContext = Record<MessageTemplateVariable, string>

// Substituicao generica de variaveis {chave} -> valor. Chaves desconhecidas
// ficam literais. Reutilizada pelo template de captacao e pela carta de intencao.
export function renderTemplate(
  template: string,
  ctx: Record<string, string>,
): string {
  return template.replace(/\{(\w+)\}/g, (match, key: string) =>
    key in ctx ? ctx[key] : match,
  )
}

export function renderMessageTemplate(
  template: string,
  ctx: MessageTemplateContext,
): string {
  return renderTemplate(template, ctx)
}

// --- Formatacao de data ---

export function formatDate(date: string | null, locale: Locale = 'pt-BR'): string {
  if (!date) return '—'
  return new Intl.DateTimeFormat(locale).format(new Date(date))
}

// --- Labels e cores de status de lead ---

export function getLeadStatusLabel(status: LeadStatus): string {
  const labels: Record<LeadStatus, string> = {
    novo: 'Novo',
    em_contacto: 'Em Contato',
    negociando: 'Negociando',
    fechado: 'Fechado',
    perdido: 'Perdido',
  }
  return labels[status]
}

export function getLeadStatusColor(status: LeadStatus): string {
  const colors: Record<LeadStatus, string> = {
    novo: 'bg-[#DBEAFE] text-[#1E40AF]',
    em_contacto: 'bg-[#FEF9C3] text-[#854D0E]',
    negociando: 'bg-[#FFEDD5] text-[#9A3412]',
    fechado: 'bg-[#DCFCE7] text-[#166534]',
    perdido: 'bg-[#F1F5F9] text-[#475569]',
  }
  return colors[status]
}

// --- Labels de condicao (i18n-aware) ---

export function getCondicaoLabel(condicao: CondicaoTipo, t?: Pick<Translations, 'labels'>): string {
  if (t) {
    const map: Record<CondicaoTipo, string> = {
      novo: t.labels.conditionNew,
      semi_novo: t.labels.conditionSemiNew,
      usado: t.labels.conditionUsed,
    }
    return map[condicao]
  }
  // Fallback for admin (no translations)
  const labels: Record<CondicaoTipo, string> = {
    novo: 'Novo',
    semi_novo: 'Semi-novo',
    usado: 'Usado',
  }
  return labels[condicao]
}

// --- Formatacao de data relativa ---

export function formatRelativeDate(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffHours < 1) return 'Agora'
  if (diffHours < 24) return `Ha ${diffHours}h`
  if (diffDays === 1) return 'Ontem'
  if (diffDays < 7) return `Ha ${diffDays} dias`
  return formatDate(dateStr)
}

// --- Labels de combustivel (i18n-aware) ---

export function getCombustivelLabel(combustivel: CombustivelTipo, t?: Pick<Translations, 'labels'>): string {
  if (t) {
    const map: Record<CombustivelTipo, string> = {
      avgas: t.labels.fuelAvgas,
      jet_a1: t.labels.fuelJetA1,
      diesel: t.labels.fuelDiesel,
      gasolina: t.labels.fuelGasoline,
      outro: t.labels.fuelOther,
    }
    return map[combustivel]
  }
  // Fallback for admin (no translations)
  const labels: Record<CombustivelTipo, string> = {
    avgas: 'AVGAS',
    jet_a1: 'JET-A1',
    diesel: 'Diesel',
    gasolina: 'Gasolina',
    outro: 'Outro',
  }
  return labels[combustivel]
}

// --- Fabricante resolver (code dictionary; categorias/statuses are DB JSONB now) ---

type TaxonomyLookup = Record<string, string>

export function getFabricanteLabel(
  fabricante: { nome: string; slug?: string | null } | null | undefined,
  t?: Pick<Translations, 'fabricantes'>,
): string {
  if (!fabricante) return ''
  if (!t) return fabricante.nome
  const key = fabricante.slug ? slugify(fabricante.slug) : slugify(fabricante.nome)
  const map = t.fabricantes as TaxonomyLookup
  return map[key] ?? fabricante.nome
}

// --- Formatacao de tamanho de ficheiro ---

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
